<script lang="ts">
	/**
	 * `/Settings` and everything under it, including the whole of administration.
	 *
	 * The overview and the audit log always lived here, rendered by this layout and reached from the
	 * second-level sidebar below. The rest of the admin screens sat at a top-level `/Admin/*` behind
	 * a tab strip of their own, so the store had two administrative surfaces that looked nothing
	 * alike, and neither of them listed the other's pages. They are one surface now: the second-level
	 * sidebar is **the** list of admin pages, and the tab strip is gone rather than repeated beside
	 * it.
	 *
	 * Two levels of highlight, and they answer different questions. The first-level item asks "which
	 * section am I in", so every `/Settings/Admin/*` page collapses onto `Admin`. The second-level
	 * item asks "which page", which is `activeSidebarPath` — the longest declared link the location
	 * sits under, so `/Settings/Admin/Customers/:uuid` lights Customers and
	 * `/Settings/Admin/Settings/Providers` lights Payment providers rather than its parent.
	 */

	import { Sidebar, SidebarWrapper, SidebarGroup, SidebarItem } from 'flowbite-svelte';
	import Router, { push, location } from '../svelte-spa-router';
	import { Icon } from 'flowbite-svelte-icons';
	import { isAuthorized } from '../AbstractID';
	import AbstractID from '../AbstractID';
	import settingsRoutes from './settings/routes';
	import site from '../site';
	import { activeSidebarPath } from '../Utilities';
	import PageHeader from '../components/PageHeader.svelte';

	// NOTE: We need to export `params` to allow the SPA router to get the value
	export let params: any = {};
	// NOTE: silence warning
	params = params;

	/**
	 * The admin pages, in the order they are offered.
	 *
	 * This is the only list of them: a route declared in `settings/routes.js` and missing here
	 * renders perfectly and can be reached only by typing its URL, which reads as the page not
	 * existing. `AdminNavigationAgreement.test.ts` is what makes the two agree.
	 *
	 * Detail pages — a customer, a sale — are deliberately absent: each is reached from its own
	 * list, and there is no single URL for a sidebar to offer.
	 */
	const adminPages = [
		// Overview *is* the store-setup page: readiness leads it, because "is this store selling?"
		// is the question an operator arrives with, and it was one click further away than a list
		// of user counts.
		{ href: '/Settings/Admin', label: 'Overview', icon: 'gauge' },
		{ href: '/Settings/Admin/Catalog', label: 'Catalogue', icon: 'box' },
		{ href: '/Settings/Admin/Settings', label: 'Store settings', icon: 'sliders' },
		{ href: '/Settings/Admin/Settings/Providers', label: 'Payment providers', icon: 'credit-card' },
		{ href: '/Settings/Admin/Settings/Currencies', label: 'Currencies', icon: 'coins' },
		{ href: '/Settings/Admin/Settings/Tax', label: 'Tax', icon: 'percent' },
		{ href: '/Settings/Admin/Settings/Refunds', label: 'Refund policy', icon: 'rotate-left' },
		{ href: '/Settings/Admin/Settings/Sellers', label: 'Sellers', icon: 'building' },
		{ href: '/Settings/Admin/Settings/Email', label: 'Email', icon: 'envelope' },
		{ href: '/Settings/Admin/Applications', label: 'Applications', icon: 'plug' },
		// One list of people. `/Settings/Admin/Users` listed the same accounts from the other end —
		// the role rather than the purchases — so an administrator had to know which fact they were
		// after before they could choose a screen. It is gone rather than aliased, exactly as the
		// old `/Admin/*` paths were: nothing outside the app links to it, and an alias is a second
		// door that goes stale in silence.
		{ href: '/Settings/Admin/Customers', label: 'Customers', icon: 'user-tag' },
		// Store-wide, as opposed to the per-customer lists on a customer's own page: "which
		// subscriptions are past due" cannot be answered by visiting every customer in turn.
		{ href: '/Settings/Admin/Transactions', label: 'Transactions', icon: 'receipt' },
		// §12. Beside Operations rather than inside it: the machinery's health and the store's
		// performance are read by different people at different moments, and a heartbeat table
		// between a funnel and a revenue chart makes the page about neither.
		{ href: '/Settings/Admin/SalesFunnel', label: 'Sales Funnel', icon: 'filter' },
		// §13. Beside Analytics rather than inside it: analytics reads the visit log and answers
		// "what did people do", this reads the money's own tables and answers "what did the store
		// take". They are read by different people, at different moments, for different reasons.
		{ href: '/Settings/Admin/Reports', label: 'Reports', icon: 'file-invoice-dollar' },
		// §4.6. The register, beside the reports rather than inside them: Reports reads the ledger
		// and therefore only ever sees invoices that were paid, so a draft, an unpaid one, a voided
		// one and a written-off one appeared on no admin screen at all.
		{ href: '/Settings/Admin/Invoices', label: 'Invoices', icon: 'file-invoice' },
		{ href: '/Settings/Admin/Operations', label: 'Operations', icon: 'heart-pulse' },
		// §13. Beside Operations rather than inside it: this is a one-off procedure with an
		// irreversible step in it, not something read during an incident.
		{ href: '/Settings/Admin/Migration', label: 'Migration', icon: 'truck-ramp-box' },
		{ href: '/Settings/Admin/Logs', label: 'Audit logs', icon: 'list-check' }
	];

	const adminHrefs = adminPages.map(page => page.href);

	let isAdminSection: boolean = false;
	let activeSectionURL: string = '';
	let activeAdminURL: string = '';
	$: {
		isAdminSection = $location === '/Settings/Admin' || $location.startsWith('/Settings/Admin/');
		activeSectionURL = isAdminSection ? '/Settings/Admin' : $location;
		activeAdminURL = activeSidebarPath($location, adminHrefs);
	}

	const fnNavigate = (event: Event, href: string) => {
		event.preventDefault();
		push(href);
	};
</script>

<!--
	The section header, and only outside administration. Every admin page carries a `PageHeader` of
	its own — it has to, since "Settings" does not say which of sixteen screens you are looking at —
	and `PageHeader` is an `h1` with the accent underline under it, so rendering both stacks two
	page titles in a column and gives the page two `h1`s.
-->
{#if !isAdminSection}
	<PageHeader>
		<slot slot="header">
			<Icon name="cog-outline" class="inline w-12 h-12 pb-2 mr-2" />Settings
		</slot>
		<slot slot="description">
			Manage and adjust the {site.name} settings.
		</slot>
	</PageHeader>
{/if}

<div class="lg:flex mt-10">
	<Sidebar asideClass="lg:w-52 flex-none" activeUrl={activeSectionURL}>
		{@const activeClass = "flex items-center p-1.5 text-sm font-semibold rounded-lg dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700 text-primary-500 bg-primary-100 dark:bg-primary-600/20 my-0.5 active"}
		{@const nonActiveClass = "flex items-center p-1.5 text-sm font-medium text-gray-700 rounded-lg dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 my-0.5"}
		{@const iconClass = "w-5 h-5 min-w-5 min-h-5 pt-1 shrink-0 transition duration-75 group-hover:text-gray-900 dark:group-hover:text-white"}
		<!-- The admin list uses Font Awesome, as the app sidebar does, because several of these
		     pages have no counterpart in the flowbite icon set. `w-5` on both keeps the two lists
		     on one vertical axis. -->
		{@const faIconClass = "w-5 shrink-0 text-center transition duration-75 group-hover:text-gray-900 dark:group-hover:text-white"}

		<SidebarWrapper divClass="overflow-y-auto py-4 px-3 rounded-2xl bg-depth">
			<SidebarGroup>
				{@const hrefAccount = '/Settings/Account'}
				{@const hrefAdmin = '/Settings/Admin'}

				<SidebarItem {activeClass} {nonActiveClass} label="Account" href={hrefAccount} on:click={(event) => fnNavigate(event, hrefAccount)}>
					<svelte:fragment slot="icon">
						<Icon name="user-outline" class={iconClass} />
					</svelte:fragment>
				</SidebarItem>

				{#if $isAuthorized && AbstractID.isAdministrator}
					<SidebarItem {activeClass} {nonActiveClass} label="Admin" href={hrefAdmin} on:click={(event) => fnNavigate(event, hrefAdmin)}>
						<svelte:fragment slot="icon">
							<Icon name="cog-outline" class={iconClass} />
						</svelte:fragment>
					</SidebarItem>
				{/if}
			</SidebarGroup>
		</SidebarWrapper>

		{#if $isAuthorized && AbstractID.isAdministrator && isAdminSection}
			<Sidebar asideClass="lg:w-52 flex-none" activeUrl={activeAdminURL}>
				<SidebarWrapper divClass="overflow-y-auto py-4 px-3 rounded-2xl bg-depth mt-4">
					<SidebarGroup>
						{#each adminPages as page (page.href)}
							<SidebarItem
								{activeClass}
								{nonActiveClass}
								label={page.label}
								href={page.href}
								on:click={(event) => fnNavigate(event, page.href)}
							>
								<svelte:fragment slot="icon">
									<i class="fa-solid fa-{page.icon} {faIconClass}" />
								</svelte:fragment>
							</SidebarItem>
						{/each}
					</SidebarGroup>
				</SidebarWrapper>
			</Sidebar>
		{/if}
	</Sidebar>
	<div class="lg:ml-10 lg:mt-0 mt-10 w-full">
		<Router routes={settingsRoutes} />
	</div>
</div>
